import { useEffect, useState } from "react";
import { createFileRoute, redirect, Outlet } from "@tanstack/react-router";
import { fetchSession, sessionQueryKey, appEnv } from "@/lib/auth";
import { BreadcrumbProvider } from "@/components/shell/BreadcrumbContext";
import { EnvBanner } from "@/components/shell/EnvBanner";
import { Sidebar } from "@/components/shell/Sidebar";
import { Topbar } from "@/components/shell/Topbar";
import { KeyboardShortcutsDialog } from "@/components/shell/KeyboardShortcutsDialog";
import { NewVersionNotice } from "@/components/shell/NewVersionNotice";
import { useNewVersionAvailable } from "@/lib/newVersion";

// Protected layout. The beforeLoad guard ensures a session before any child
// route loads; unauthenticated users are redirected to /login.
export const Route = createFileRoute("/_shell")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { session };
  },
  component: ShellLayout,
});

const COLLAPSE_KEY = "avds-sidebar-collapsed";

function ShellLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Hand ownership of the collapsed state from the FOUC-guard class to React.
  useEffect(() => {
    document.documentElement.classList.remove("avds-sidebar-collapsed-init");
  }, []);

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* privacy mode */
      }
      return next;
    });
  }

  const showEnvBanner = appEnv() !== "production";
  const newVersion = useNewVersionAvailable();

  return (
    <BreadcrumbProvider>
      <div
        className={`avds-shell${collapsed ? " is-collapsed" : ""}${showEnvBanner ? " has-env-banner" : ""}`}
      >
        {showEnvBanner && <EnvBanner />}
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        <Topbar />
        <main className="avds-main">
          <Outlet />
        </main>
        <KeyboardShortcutsDialog />
        {newVersion && <NewVersionNotice />}
      </div>
    </BreadcrumbProvider>
  );
}
